import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Chip } from "@mui/material";
import useSelector from 'hooks/useSelector';
import { DocumentSearchQuery, DateRange, DocumentSearchReplaceSuggestion } from 'shared/models/api/models';
import { documentSearchReplaceSuggestions } from 'shared/services/railDocumentsApi';
import { documentModuleDisplay } from 'shared/enums/document-module-enum';
import { documentTypeDisplay, DocumentTypeEnum } from 'shared/enums/document-type-enum';
import { DocumentStatusEnum } from 'shared/enums/document-status-enum';
import { documentSearchState } from './DocumentSearchState';

import { TodayOutlined, EventOutlined } from '@mui/icons-material';

import './DocumentSearch.css';

import {
    Input,
    Label,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu
} from 'reactstrap';

import {
    Search as SearchIcon,
    Tune as TuneIcon,
    Sync as SyncIcon,
    ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { HighlightOff as ClearIcon } from '@mui/icons-material';
import { getUserExistingRulebookMode } from '../../../../shared/store/applicationState';
import { RulebookEnumToString, RulebookModeEnum } from '../../../../shared/enums/rulebook-mode-enum';

interface FilterItem {
    id: number;
    label: string;
}

interface FilterDropdownParams {
    items: FilterItem[];
    checkedItems: number[];
    label: string;
    keyPrefix: string;
    toggleFunction(id: number): any;
}

export const FilterDropdown = (props: FilterDropdownParams) => {

    //Prevent React warning when onChange function is abscent
    const doNothing = () => { };

    return (
        <UncontrolledDropdown setActiveFromChild>
            <DropdownToggle tag="div" className="filter-results-button">{props.label}<ExpandIcon /></DropdownToggle>
            <DropdownMenu>
                {props.items.map((item) => (
                    <div key={`${props.keyPrefix}-${item.id}`} className="form-check-inline dropdown-item" onClick={() => props.toggleFunction(item.id)}>
                        <Input type="checkbox" className="form-check-input mb-1 ml-2" checked={props.checkedItems.includes(item.id)} onChange={doNothing} />
                        <Label className="form-check-label">{item.label}</Label>
                    </div>
                ))}
            </DropdownMenu>
        </UncontrolledDropdown>
    );
}


interface IDateRangePickerProps {
    className?: string;
    fromDate?: Date;
    toDate?: Date;
    setter: (fromDate?: Date | null, toDate?: Date | null) => void;
}

const DateRangePicker = (props: IDateRangePickerProps) => {
    return (
        <div className={props.className}>

            <label>
                <label className="pr-2">From:</label>
                <DatePicker
                    className="ml-2 mr-2"
                    dateFormat="dd/MM/yyyy"
                    selected={props.fromDate}
                    maxDate={props.toDate}
                    onChange={(d: Date | null) => props.setter(d, props.toDate)}
                />
                <TodayOutlined />
            </label>

            <label>
                <label className="pr-2">To:</label>
                <DatePicker
                    className="ml-2 mr-2"
                    dateFormat="dd/MM/yyyy"
                    selected={props.toDate}
                    minDate={props.fromDate}
                    onChange={(d: Date | null) => props.setter(props.fromDate, d)}
                />
                <EventOutlined />
            </label>

        </div>
    );
};


interface DateRangeDropdownParams {
    searchQuery: DocumentSearchQuery;
    setSearchQuery(query: DocumentSearchQuery): any;
}

export const DateRangeDropdown = (props: DateRangeDropdownParams) => {
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());

    const setDates = (startDate: Date, endDate: Date) => {
        if (startDate === undefined || endDate === undefined) {
            return;
        }
        const newQuery = { ...props.searchQuery, dateRange: { startDate: startDate, endDate: endDate } };
        props.setSearchQuery(newQuery);
    }

    const clearDates = () => {
        const newQuery = { ...props.searchQuery, dateRange: undefined };
        props.setSearchQuery(newQuery);
    }

    const setDateRange = (dateFrom: Date | undefined | null, dateTo: Date | undefined | null): void => {
        setFromDate(dateFrom ? dateFrom : new Date());
        setToDate(dateTo ? dateTo : new Date());
    };

    return (
        <UncontrolledDropdown setActiveFromChild>
            <DropdownToggle tag="div" className="filter-results-button">Published date<ExpandIcon /></DropdownToggle>
            <DropdownMenu className="search-date-range">
                <div key={`dateRange`} className="p-2">
                    <DateRangePicker
                        fromDate={fromDate ?? undefined}
                        toDate={toDate ?? undefined}
                        setter={(dateFrom, dateTo) => { setDateRange(dateFrom, dateTo) }}
                    />
                    <div className="search-date-button p-1 pl-2 pr-4 " onClick={clearDates}><button className="btn btn-secondary">Clear</button></div>
                    <div className="search-date-button p-1" onClick={() => setDates(fromDate, toDate)}><button className="btn btn-primary btn-secondary">Apply</button></div>
                </div>
            </DropdownMenu>
        </UncontrolledDropdown>
    );
}

interface RefineSearchParams {
    query: DocumentSearchQuery;
    minSearchTermLength: number;
}

export const RefineSearch = (props: RefineSearchParams) => {
    const [searchTerm, setSearchTerm] = useState(props.query.searchTerm);
    const [searchQuery, setSearchQuery] = useState({ searchTerm: props.query.searchTerm } as DocumentSearchQuery);
    const [searchHistory, setSearchHistory] = useState([] as string[]);
    const [searchHistoryLoaded, setSearchHistoryLoaded] = useState(false);
    const [runPostMount, setRunPostMount] = useState(false);

    const [allSuggestions, setAllSuggestions] = useState([] as DocumentSearchReplaceSuggestion[]);
    const [currentSuggestions, setCurrentSuggestions] = useState([] as DocumentSearchReplaceSuggestion[]);
    const mainSearchBarRef = React.createRef() as React.RefObject<HTMLInputElement>;
    
    const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof e.target.value === 'string') {
            setSearchTerm(e.target.value);
            let query = searchQuery;
            query.searchTerm = e.target.value.trim();
            setSearchQuery(query);
            updateSuggestions(e.target.value);
        }
    }

    const updateSuggestions = (searchTerm: string) => {
        let formattedSearchTerm = ` ${searchTerm.toLowerCase()}`;
        let newSuggestions = allSuggestions.filter(rs => {
            let formattedExpression = ` ${rs.expression.toLowerCase()}`;
            return formattedSearchTerm.endsWith(formattedExpression);
        });
        setCurrentSuggestions(newSuggestions);
    }

    const selectSuggestion = (rs: DocumentSearchReplaceSuggestion) => {
        setCurrentSuggestions([]);
        let query = searchQuery;
        let replaceIndex = query.searchTerm.toLowerCase().lastIndexOf(rs.expression.toLowerCase());
        if (replaceIndex > -1) {
            query.searchTerm = `${query.searchTerm.substring(0, replaceIndex)}${rs.suggestion}`;
        }
        setSearchQuery(query);
        setSearchTerm(`${query.searchTerm} `);
        mainSearchBarRef.current?.focus();
    }

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            search();
        }
    }

    const rulebookMode = getUserExistingRulebookMode();
    const search = () => {
        if (searchQuery.searchTerm.length < props.minSearchTermLength) {
            return;
        }       
        searchQuery.mode = rulebookMode!;
        addSearchHistory(searchQuery.searchTerm);
        documentSearchState.setState(q => {
            q.query = searchQuery;
            q.runQuery = true;
            q.pushHistory = true;
        });
    }

    const searchForTerm = (term: string) => {
        if (term.length < props.minSearchTermLength) {
            return;
        }                
        const query = { ...searchQuery, searchTerm: term };
        setSearchQuery(query);
        setSearchTerm(query.searchTerm);
        addSearchHistory(query.searchTerm);
        documentSearchState.setState(q => {
            q.query = query;
            q.runQuery = true;
            q.pushHistory = true;
        });
    }

    const documentTypes = (useSelector(x => x.searchableRailDocumentTypes) || []);
    const documentModules = [...new Set(documentTypes.map(type => type.documentModule))]

    const documentFilterModules = documentModules.map((module) => {
        let item: FilterItem = { id: module, label: documentModuleDisplay(module) };
        return item;
    });

    const documentFilterTypes = documentTypes.map((type) => {
        let item: FilterItem = { id: type.typeId, label: documentTypeDisplay(type.typeId) };
        return item;
    });

    const getDocumentFilterStatus = (): FilterItem[] => {
        let items: FilterItem[] = [];
        Object.values(DocumentStatusEnum).map((status) => {
            const statusId = Number(status);
            if (!isNaN(statusId)) {
                if (statusId < DocumentStatusEnum.DeletedFull) {
                    let item: FilterItem = { id: statusId, label: DocumentStatusEnum[statusId] };
                    items.push(item);
                }
            }
        });
        return items;
    };

    const documentFilterStatus = getDocumentFilterStatus();

    function removeItem<T>(arr: Array<T>, value: T): Array<T> {
        const index = arr.indexOf(value);
        if (index > -1) {
            arr.splice(index, 1);
        }
        return arr;
    }

    const toggleModule = (id: number) => {
        let moduleIds = searchQuery.moduleIds;
        if (moduleIds === undefined) {
            moduleIds = [];
        }
        if (moduleIds.includes(id)) {
            removeItem(moduleIds, id);
        }
        else {
            moduleIds.push(id);
        }
        setSearchQuery({ ...searchQuery, moduleIds: moduleIds });
    }

    const toggleType = (id: number) => {
        let typeIds = searchQuery.typeIds;
        if (typeIds === undefined) {
            typeIds = [];
        }
        if (typeIds.includes(id)) {
            removeItem(typeIds, id);
        }
        else {
            typeIds.push(id);
        }
        setSearchQuery({ ...searchQuery, typeIds: typeIds });
    }

    const toggleStatus = (id: number) => {
        let statusIds = searchQuery.statusIds;
        if (statusIds === undefined) {
            statusIds = [];
        }
        if (statusIds.includes(id)) {
            removeItem(statusIds, id);
        }
        else {
            statusIds.push(id);
        }
        setSearchQuery({ ...searchQuery, statusIds: statusIds });
    }

    const clearSearchQuery = () => {
        setSearchTerm("");
        setSearchQuery({} as DocumentSearchQuery);
    }

    const clearDates = () => {
        setSearchQuery({ ...searchQuery, dateRange: undefined });
    }

    const displayDateRange = (dateRange: DateRange) => {
        return `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`;
    }

    const addSearchHistory = (searchTerm: string) => {
        if (searchTerm.length < 1 || (searchHistory.length > 0 && searchTerm === searchHistory[0])) {
            return;
        }

        const history = searchHistory;

        const index = history.indexOf(searchTerm);
        if (index > -1) {
            history.splice(index, 1);
        }

        history.unshift(searchTerm);
        if (history.length > 8) {
            history.pop();
        }
        const historyJson = JSON.stringify(history);
        localStorage.setItem('searchHistory', historyJson);
    }

    const loadHistory = () => {
        const historyJson = localStorage.getItem('searchHistory');
        if (typeof (historyJson) === 'string') {
            const history: string[] = JSON.parse(historyJson);
            setSearchHistory(history);
        }
        else {
            const history: string[] = [];
            setSearchHistory(history);
            const historyJson = JSON.stringify(history);
            localStorage.setItem('searchHistory', historyJson);
        }
    };

    useEffect(() => {
        if (runPostMount) {
            addSearchHistory(props.query.searchTerm);
            setRunPostMount(false);
        }

        if (!searchHistoryLoaded) {
            loadHistory();
            setSearchHistoryLoaded(true);
            setRunPostMount(true);
        }

    }, [runPostMount]);

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await documentSearchReplaceSuggestions();

            if (mounted) {
                setAllSuggestions(results);
            }
        };

        mount();
        return () => { mounted = false };
    }, []);

    return (
        <div className="refine-search">
            <div className="refine-search-label">Review or refine your search:</div>

            <div className="mt-2 mb-2 position-relative">
                <div className="refine-search-icon">
                    <SearchIcon fontSize="inherit" />
                </div>
                <div className="btn btn-primary btn-secondary ml-2 pl-4 pr-4 float-right" onClick={search}>Search</div>
                <div className="search-border w-auto">
                    <input autoFocus
                        type="text"
                        name="search"
                        id="search"
                        className="main-search-bar"
                        ref={mainSearchBarRef}
                        placeholder="Search"
                        maxLength={500}
                        autoComplete="off"
                        value={searchTerm}
                        onKeyDown={handleKeyDown}
                        onChange={searchOnChange} />
                    <div className="main-search-clear" onClick={clearSearchQuery}><ClearIcon /></div>
                    {currentSuggestions.length > 0 &&
                        <div className="search-suggestions">
                            {currentSuggestions.map((rs, i) => (
                                <div key={i} onClick={() => selectSuggestion(rs)}>
                                    <label>{rs.suggestion}</label>
                                </div>
                            ))}
                        </div>
                    }
                </div>
            </div>

            <div className="search-chips-container mt-2 mb-2">
                <div className="main-search-chips-container">
                    {searchQuery.moduleIds?.map((moduleId) => (
                        <Chip key={`module-chip-${moduleId}`} className="m-1" label={documentModuleDisplay(moduleId)} onDelete={() => toggleModule(moduleId)} />
                    ))}
                    {searchQuery.typeIds?.map((typeId) => (
                        <Chip key={`type-chip-${typeId}`} className="m-1" label={documentTypeDisplay(typeId)} onDelete={() => toggleType(typeId)} />
                    ))}
                    {searchQuery.statusIds?.map((statusId) => (
                        <Chip key={`status-chip-${statusId}`} className="m-1" label={DocumentStatusEnum[statusId]} onDelete={() => toggleStatus(statusId)} />
                    ))}
                    {searchQuery.dateRange !== undefined &&
                        <Chip key={`status-chip-dateRange`} className="m-1" label={displayDateRange(searchQuery.dateRange)} onDelete={clearDates} />
                    }
                </div>
            </div>

            <div className="filter-results-container mt-2 mb-2">
                <div className="tune-icon">
                    <TuneIcon fontSize="inherit" className="flip-x-axis" />
                </div>
                <div className="filter-results-label">Filter Results:</div>
                <div className="filter-results-buttons">

                    <FilterDropdown
                        items={documentFilterModules}
                        checkedItems={searchQuery.moduleIds ?? []}
                        label={'Module'}
                        keyPrefix={'module'}
                        toggleFunction={toggleModule}
                    />

                    <FilterDropdown
                        items={rulebookMode === RulebookEnumToString(RulebookModeEnum.Traditional) ?
                            documentFilterTypes.filter(t => t.id != DocumentTypeEnum.RailNetworkGuidingPrinciples) : documentFilterTypes}
                        checkedItems={searchQuery.typeIds ?? []}
                        label={'Document type'}
                        keyPrefix={'type'}
                        toggleFunction={toggleType}
                    />

                    <FilterDropdown
                        items={documentFilterStatus}
                        checkedItems={searchQuery.statusIds ?? []}
                        label={'Status'}
                        keyPrefix={'status'}
                        toggleFunction={toggleStatus}
                    />

                    <DateRangeDropdown searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

                </div>
            </div>

            {searchHistory.length > 1 &&
                <div className="recent-searches">
                    <div className="recent-searches-label">Recent searches:</div>
                    {searchHistory.map((searchTerm, i) => (
                        <React.Fragment key={`recent-search-${i}`}>
                            {i > 0 &&
                                <div onClick={() => searchForTerm(searchTerm)} className="recent-search"><SyncIcon className="flip-x-axis" />
                                    <div>Keyword:</div>
                                    <div>"{searchTerm}"</div>
                                </div>
                            }
                        </React.Fragment>
                    ))}
                </div>
            }

        </div>
    );
}
